/* eslint-disable @typescript-eslint/no-explicit-any */
import _, { isNil, isNumber } from "lodash";
import { FieldError, FieldErrors, FieldValues } from "react-hook-form";

import {
  BULK_FORM_NAME_PREFIX,
  MULTISELECT_FORM_FIELDS,
  NO_VALUE_DISPLAY,
} from "../constants";
import { requiredRuleStringField } from "../constants/validation.const";
import { BulkMultiselectActionsEnum } from "../enums";
import {
  FormError,
  ICustomField,
  IDropdownItem,
  IDropdownItemExt,
  IFormAttributes,
  ISelectListItem,
  ISimpleDto,
  ISimpleDtoBase,
  ISimplePassengerDto,
  IValidationsYupRoot,
  RydeUiUnit,
  TBulkFormNameWithPrefix,
  TDropdownItemsKeeperIndex,
  TDropdownItemsKeeperIndexKey,
} from "../models";
import { controlNameMetadataResolve } from "./dynamic-forms.helpers";
import { Log } from "./log.helpers";

const pickTrueFieldNames = (
  fields: Partial<Readonly<Record<string, any>>>,
): string[] =>
  _(fields)
    .pickBy(value => {
      return !_.isArray(value) || _.compact(value).length;
    })
    .keys()
    .value();

interface IFilterOnlyDirtyArguments<T> {
  formFields: T;
  dirtyFields: Partial<Readonly<Record<string, any>>>;
  skipMetadata?: boolean;
  siblingToValidationFields?: string[];
}

export const filterOnlyDirty = <T extends { metadata?: object }>({
  formFields,
  dirtyFields,
  skipMetadata = false,
  siblingToValidationFields = [],
}: IFilterOnlyDirtyArguments<T>): Partial<T> => {
  Log.debug(`Original dirty fields:`, dirtyFields);
  const dirty = pickTrueFieldNames(dirtyFields);
  const filteredFields = _.pick(formFields, [
    ...dirty,
    ...siblingToValidationFields,
  ]);
  Log.debug(`Filtered dirty fields:`, filteredFields);

  if (skipMetadata) {
    return filteredFields;
  }

  return filterOnlyDirtyMetadata(filteredFields, dirtyFields);
};

export const filterOnlyDirtyMetadata = <T extends { metadata?: object }>(
  formFields: T,
  dirtyFields: Partial<Readonly<Record<string, any>>>,
): Partial<T> => {
  const filteredFields = { ...formFields };

  if (filteredFields.metadata) {
    const dirtyMetadata = pickTrueFieldNames(dirtyFields.metadata);
    filteredFields.metadata = _.pick(filteredFields.metadata, dirtyMetadata);
  }

  Log.debug(`Filtered dirty metadata fields:`, filteredFields);

  return filteredFields;
};

export const filterDirtyOrDefaultMetadata = <T extends { metadata?: object }>(
  formFields: T,
  defaultFields: Partial<T> | undefined,
  dirtyFields: Partial<Readonly<Record<string, any>>>,
): Partial<T> => {
  const filteredFields = { ...formFields };

  if (filteredFields.metadata) {
    const dirtyMetadata = pickTrueFieldNames(dirtyFields.metadata);
    const baseMetadata = _(defaultFields?.metadata || {})
      .pickBy(value => {
        return !_.isArray(value) || !!value;
      })
      .keys()
      .value();
    filteredFields.metadata = _.pick(
      formFields.metadata,
      _.union(dirtyMetadata, baseMetadata),
    );
    Log.debug(`Filtered dirty and default metadata fields:`, filteredFields);
  }

  return filteredFields;
};

interface IRegisterAttributes {
  readonly: boolean;
  disabled: boolean;
}

/** TODO refactor this
 * "<Controller rules ..." <- not allowed none of these properties ("readonly", "disabled")
 * */
export const getRegisterAttributes = (
  formAttributes: IFormAttributes | null | undefined,
): IRegisterAttributes => {
  const registerAttributes = {
    readonly: Boolean(formAttributes?.readonly),
    disabled: Boolean(formAttributes?.disabled),
  };

  return registerAttributes;
};

export const trimDefaultValues = (
  data: Record<string, any>,
): Record<string, any> => {
  if (!data) {
    return data;
  }

  for (const valueKey in data) {
    if (!Object.hasOwn(data, valueKey)) {
      continue;
    }

    const value = data[valueKey];

    if (
      value === "" ||
      value === null ||
      value === undefined ||
      (Object.keys(value).length === 0 && value.constructor === Object) ||
      valueKey === "createdAt" ||
      valueKey === "updatedAt"
    ) {
      delete data[valueKey];
    }
  }

  return data;
};

export const prepareDataForForm = (
  data: Record<string, any> | undefined,
): Record<string, any> | undefined => {
  if (!data) {
    return data;
  }

  const tmp: Record<string, any> = {};

  for (const key in data) {
    if (!(key === "createdAt" || key === "updatedAt" || key === "deletedAt")) {
      const value = data[key];

      if (value === null) {
        tmp[key] = "";
      } else {
        tmp[key] = value;
      }
    }
  }

  return tmp;
};

const errorIsFormError = (
  err: string | Partial<FieldError> | null,
): err is FieldError => {
  if (!err || typeof err === "string") {
    return false;
  }

  return (err as FieldError)?.message !== undefined;
};

export const formErrorToString = (err: FormError): string => {
  if (!err) {
    return "";
  }

  if (typeof err === "string") {
    return err;
  }

  if (errorIsFormError(err)) {
    return err?.message || "";
  }

  return String(err);
};

export const isSimpleDtoBase = (item: unknown): item is ISimpleDtoBase => {
  return !!item && !!(item as ISimpleDtoBase)?.id;
};

export const isSimpleDto = (item: unknown): item is ISimpleDto => {
  return !!item && !!(item as ISimpleDto)?.id && !!(item as ISimpleDto)?.name;
};

export const isSimplePassengerDto = (
  item: unknown,
): item is ISimplePassengerDto => {
  return (
    !!item &&
    !!(item as ISimplePassengerDto)?.id &&
    !!(item as ISimplePassengerDto)?.lastName
  );
};

export const simpleDtoToDropdownItem = (dto: ISimpleDto): IDropdownItem => {
  return { ...dto, value: dto.id, label: `${dto.name}` };
};

export const simplePassengerDtoToDropdownItem = (
  dto: ISimplePassengerDto,
): IDropdownItem => {
  return { ...dto, value: dto.id, label: `${dto.lastName} ${dto.firstName}` };
};

export type DropdownResolverName =
  "simpleDto" | "simplePassengerDto" | "tagDto" | "default";

const resolvers: Record<
  DropdownResolverName,
  (dtoItem: unknown) => IDropdownItem
> = {
  simpleDto: dtoItem => simpleDtoToDropdownItem(dtoItem as ISimpleDto),
  simplePassengerDto: dtoItem =>
    simplePassengerDtoToDropdownItem(dtoItem as ISimplePassengerDto),
  tagDto: dtoItem =>
    ({
      ...(dtoItem || {}),
      value: (dtoItem as ISimpleDto).name,
      label: `${(dtoItem as ISimpleDto).name}`,
    }) as IDropdownItem,
  default: dtoItem =>
    ({
      ...(dtoItem || {}),
      value: (dtoItem as ISimpleDtoBase).id,
      label: `${(dtoItem as ISimpleDtoBase).id}`,
    }) as IDropdownItem,
};

interface IGetResolverNameArguments {
  dropdownItem: unknown | undefined;
  entity: RydeUiUnit;
}

const getResolverName = ({
  dropdownItem,
  entity,
}: IGetResolverNameArguments): DropdownResolverName => {
  switch (true) {
    case entity === "systemTag":
    case isSimpleDto(dropdownItem):
      return "simpleDto";
    case isSimplePassengerDto(dropdownItem):
      return "simplePassengerDto";
    default:
      return "default";
  }
};

interface IGetDropdownItemResolverArguments {
  dropdownItem: unknown | undefined;
  entity: RydeUiUnit;
  forceResolverName?: DropdownResolverName;
}

const getDropdownItemResolver = ({
  dropdownItem,
  entity,
  forceResolverName,
}: IGetDropdownItemResolverArguments): ((
  dtoItem: unknown,
) => IDropdownItem) => {
  const resolverName = forceResolverName
    ? forceResolverName
    : getResolverName({
        dropdownItem,
        entity,
      });
  return resolvers[resolverName];
};

interface ITransformDropdownDtosArguments {
  dtos: unknown[] | undefined;
  entity: RydeUiUnit;
  forceResolverName?: DropdownResolverName;
}

export const transformDropdownDtos = ({
  dtos,
  entity,
  forceResolverName,
}: ITransformDropdownDtosArguments): IDropdownItem[] => {
  const items: IDropdownItem[] | undefined = dtos
    ?.filter(x => isSimpleDtoBase(x))
    .map(dropdownItem => {
      const resolver = getDropdownItemResolver({
        dropdownItem,
        entity,
        forceResolverName,
      });
      return resolver(dropdownItem);
    });
  return items || [];
};

interface IFilterActiveDropdownItemsArguments<T> {
  items: T[] | undefined;
  activeFieldName?: string;
  isSelected?: (item: T) => boolean;
}

export const filterActiveDropdownItems = <
  T extends IDropdownItem | ISelectListItem,
>({
  items,
  activeFieldName,
  isSelected,
}: IFilterActiveDropdownItemsArguments<T>): T[] | undefined => {
  const activeField = activeFieldName || "isActive";

  return items?.filter(item => {
    const isActive = (item as IDropdownItemExt)[activeField];

    if (typeof isActive === "undefined") {
      return true;
    }

    return Boolean(isActive) || Boolean(isSelected?.(item));
  });
};

export const isNestedField = (
  fieldName: string | null | undefined,
): boolean => {
  if (!fieldName) {
    return false;
  }

  return fieldName.includes(".");
};

const getDropdownItemsKeeperIndexKey = (
  entity: RydeUiUnit,
  id: number,
): TDropdownItemsKeeperIndexKey =>
  `${entity}_${id}` as TDropdownItemsKeeperIndexKey;

interface IIndexationDropdownItemsKeeperIndexArguments {
  entity: RydeUiUnit;
  dropdownList: IDropdownItem[] | ISelectListItem[];
  dropdownItemsKeeperIndex: TDropdownItemsKeeperIndex;
}

const clearDropdownItemsKeeperIndexForEntity = ({
  dropdownItemsKeeperIndex,
  entity,
}: {
  dropdownItemsKeeperIndex: TDropdownItemsKeeperIndex;
  entity: RydeUiUnit;
}): void => {
  for (const key of [...dropdownItemsKeeperIndex.keys()]) {
    const separatorIndex = key.lastIndexOf("_");
    if (separatorIndex === -1) {
      continue;
    }

    if (key.slice(0, separatorIndex) === entity) {
      dropdownItemsKeeperIndex.delete(key);
    }
  }
};

const indexationDropdownItemsKeeperIndex = ({
  dropdownList,
  dropdownItemsKeeperIndex,
  entity,
}: IIndexationDropdownItemsKeeperIndexArguments): void => {
  clearDropdownItemsKeeperIndexForEntity({
    dropdownItemsKeeperIndex,
    entity,
  });

  dropdownList.forEach((dropdownItem, index) => {
    if (!isNumber(dropdownItem.value)) {
      Log.warn(
        `Trying to add to index unsupported value for entity: ${entity} value: ${dropdownItem.value}`,
      );
      return;
    }

    const key = getDropdownItemsKeeperIndexKey(entity, dropdownItem.value);
    dropdownItemsKeeperIndex.set(key, index);
  });
};

interface IGetDropdownOptionLabelNEWArguments {
  entity: RydeUiUnit;
  dropdownList: IDropdownItem[] | ISelectListItem[] | undefined;
  id: number | null | undefined;
  dropdownItemsKeeperIndex: TDropdownItemsKeeperIndex;
  isForceIndexation?: boolean;
}

export const getDropdownOptionLabel = ({
  entity,
  dropdownList,
  id,
  dropdownItemsKeeperIndex,
  isForceIndexation,
}: IGetDropdownOptionLabelNEWArguments): string => {
  if (!id || !+id || !dropdownList?.length) {
    return NO_VALUE_DISPLAY;
  }

  const key = getDropdownItemsKeeperIndexKey(entity, id);
  const cachedIndex = dropdownItemsKeeperIndex.get(key);
  const cachedItem = !isNil(cachedIndex)
    ? dropdownList[cachedIndex]
    : undefined;

  if (
    !isForceIndexation &&
    cachedItem &&
    String(cachedItem.value) === String(id)
  ) {
    return cachedItem.label || NO_VALUE_DISPLAY;
  }

  const isStaleCache =
    !isNil(cachedIndex) &&
    (!cachedItem || String(cachedItem.value) !== String(id));

  if (
    isForceIndexation ||
    isStaleCache ||
    !dropdownItemsKeeperIndex.size ||
    isNil(cachedIndex)
  ) {
    indexationDropdownItemsKeeperIndex({
      dropdownItemsKeeperIndex,
      dropdownList,
      entity,
    });
  }

  const index = dropdownItemsKeeperIndex.get(key);
  if (!isNil(index)) {
    const dropDownItem = dropdownList[index];
    if (dropDownItem && String(dropDownItem.value) === String(id)) {
      return dropDownItem.label || NO_VALUE_DISPLAY;
    }
  }

  const foundIndex = dropdownList.findIndex(
    item => String(item.value) === String(id),
  );
  if (foundIndex >= 0) {
    dropdownItemsKeeperIndex.set(key, foundIndex);
    return dropdownList[foundIndex]?.label || NO_VALUE_DISPLAY;
  }

  return NO_VALUE_DISPLAY;
};

export const inputValueAsNumberOrEmptyString = (value: string): number | "" => {
  if (value === "" || isNil(value)) {
    return "";
  }

  return +value.replace(/[^\-0-9.]/g, "");
};

export const checkCleanupNestedValidation = (
  name: string,
  errors: FieldErrors<FieldValues>,
): void => {
  if (isNestedField(name)) {
    _.unset(errors, name);
  }
};

export const getBulkFormFieldName = <T extends string>(
  name: T,
): TBulkFormNameWithPrefix<T> => `${name}_${BULK_FORM_NAME_PREFIX}`;

/** ADD/REMOVE need explicit ids; REPLACE may clear with []. */
export const doesBulkActionRequireSelectedValues = (
  bulkAction: BulkMultiselectActionsEnum | string | undefined,
): boolean =>
  bulkAction === BulkMultiselectActionsEnum.ADD ||
  bulkAction === BulkMultiselectActionsEnum.REMOVE;

/** RYDE-7753: REMOVE keeps inactive/orphan ids already on selected rows in the dropdown. */
export const doesBulkActionKeepAssignedInactiveOptions = (
  bulkAction: BulkMultiselectActionsEnum | string | undefined,
): boolean => bulkAction === BulkMultiselectActionsEnum.REMOVE;

export const getBulkMultiselectValueFormValidation = (
  isBulkControlSelected: boolean,
  bulkAction: BulkMultiselectActionsEnum | string | undefined,
): IValidationsYupRoot | undefined => {
  if (!isBulkControlSelected) {
    return undefined;
  }

  if (!doesBulkActionRequireSelectedValues(bulkAction)) {
    return undefined;
  }

  return requiredRuleStringField;
};

export const isBulkMultiselectValueEmpty = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return true;
  }

  return Array.isArray(value) && value.length === 0;
};

/**
 * RYDE-7753 — chips after the user picks a bulk action.
 * ADD/REPLACE: []. REMOVE: union from selected rows. Undefined: leave the field as-is.
 * REPLACE must not prefill the union: saving that default would assign every union id
 * to every row (A has 1, B has 2 → both get [1, 2]). Union is correct for REMOVE only.
 */
export const getBulkMultiselectPrefillForAction = ({
  bulkAction,
  assignedValuesUnion,
}: {
  bulkAction: BulkMultiselectActionsEnum | string | undefined;
  assignedValuesUnion: (string | number)[];
}): (string | number)[] | undefined => {
  if (
    bulkAction === BulkMultiselectActionsEnum.ADD ||
    bulkAction === BulkMultiselectActionsEnum.REPLACE
  ) {
    return [];
  }

  if (bulkAction === BulkMultiselectActionsEnum.REMOVE) {
    return assignedValuesUnion;
  }

  return undefined;
};

interface IGetIsMultiSelectFieldByControlNameArguments {
  controls: ICustomField[];
  controlName: string;
}

export const getIsMultiSelectFieldByControlName = ({
  controlName,
  controls,
}: IGetIsMultiSelectFieldByControlNameArguments): boolean => {
  const foundControl = controls.find(
    control => controlNameMetadataResolve(control) === controlName,
  );

  if (!foundControl?.formFieldTypeId) {
    return false;
  }

  return MULTISELECT_FORM_FIELDS.includes(foundControl.formFieldTypeId);
};
